<template>
  <!-- Settings top menu. Rendered by App.vue outside the capped layout
       container so its bottom border runs the full width of the window, like
       the app bar above it. The left padding clears the navigation rail so the
       tabs still line up with the content below. -->
  <unified-pane-toolbar :show-divider="false" class="account-tabs-bar">
    <template #tabs>
      <v-tabs
        :model-value="currentTab"
        class="pane-toolbar-tabs"
        :color="primary"
        hide-slider
        style="font-size:1em"
      >
        <v-tab
          rounded="0"
          value="patients"
          :to="{ name: 'accountPatients' }"
          style="font-size:1em; text-transform: capitalize;"
        >
          <v-icon start size="small">fa-duotone fa-light fa-hospital-user</v-icon>
          <span style="font-variant: none;">{{ $t('userAccount.tabs.patients') }}{{ activePatientCount > 0 ? ` (${activePatientCount})` : '' }}</span>
        </v-tab>
        <v-tab
          rounded="0"
          value="clinical"
          :to="{ name: 'accountClinical' }"
          style="font-size:1em; text-transform: capitalize;"
        >
          <!-- Light, matching the tabs either side of it and the first-run
               dialog's header.

               This app's Font Awesome kit serves a subset, per icon AND per
               weight, so the pairing is not a free choice: `notes-medical` is
               absent in every style, and `fa-light fa-clipboard-medical` — the
               glyph that stood here in regular — renders as the placeholder
               even though `fa-light fa-floppy-disk` beside it is fine. This
               pairing was added to the kit on purpose. Check the icon actually
               draws before swapping either half of it; see drawer.vue for the
               same class of kit gap. -->
          <v-icon start size="small">fa-light fa-file-medical</v-icon>
          <span style="font-variant: none;">{{ $t('userAccount.tabs.clinical') || 'Clinical protocol' }}</span>
        </v-tab>
        <v-tab
          v-if="isAccountOwner"
          rounded="0"
          value="backups"
          data-tour="tab-backups"
          :to="{ name: 'accountBackups' }"
          style="font-size:1em; text-transform: capitalize;"
        >
          <v-icon start size="small">fa-light fa-floppy-disk</v-icon>
          <span style="font-variant: none;">{{ $t('userAccount.tabs.backups') || 'Backup' }}</span>
        </v-tab>
        <!-- THE INTEGRATIONS TAB IS DELIBERATELY NOT HERE.
             ================================================
             Retroact Connect is live, but praxio.integration_partners is empty
             — no vendor has built an integration yet. The screen it led to
             therefore had nothing to offer anybody: the best case was an empty
             list, which reads as a broken feature rather than as an absent one.

             The route (`accountIntegrations`), the screen
             (account/IntegrationSettings.vue) and its locale keys all still
             exist and still work at /account/settings/integrations. Restoring
             this is putting the <v-tab> back — nothing else. Do that when the
             first partner is registered, because from that moment the screen
             has a real switch on it and a pharmacy needs a way to reach it.

             Icon note for whoever restores it: this app's Font Awesome kit is
             subsetted per icon AND per weight (see the clinical tab above), and
             `fa-regular fa-link` is the pair this codebase proves draws —
             `fa-light fa-link` was never verified. Check it renders before
             swapping either half. -->
      </v-tabs>
    </template>
  </unified-pane-toolbar>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useAuth } from '../../composables/useAuth.js'
import { useThemeColors } from '../composables/useThemeColors.js'
import { usePatientStore } from '../composables/usePatientStore.js'
import UnifiedPaneToolbar from '../common/UnifiedPaneToolbar.vue'

defineOptions({ name: 'account-tabs-bar' })

const route = useRoute()
const { primary } = useThemeColors()
const { isAccountOwner } = useAuth()
const patientStore = usePatientStore()

/**
 * ACTIVE patients, not every row in the list below.
 *
 * The portal's account rail carries the same number beside its own Patients
 * entry, and it counts the way billing counts (praxio.patients WHERE isActive
 * = 1). An archived patient is out of the panel and out of the price, so
 * counting them here would have the two apps quote different sizes for the same
 * pharmacy -- the kind of disagreement a pharmacist reads as one of them being
 * wrong. The list under this tab still shows archived patients: it is the
 * screen where you go to bring one back.
 */
const activePatientCount = computed(() => patientStore.activePatients.value.length)

// Same mapping UserAccount uses for its own tab-dependent behaviour.
const currentTab = computed(() => {
  const routeName = route.name
  if (routeName === 'accountPatients') return 'patients'
  if (routeName === 'accountUsers' || routeName === 'accountUsersAdd' || routeName === 'accountUsersList') return 'userManagement'
  if (routeName === 'accountSettings' || routeName === 'accountCredentials' || routeName === 'accountOrganization' || routeName === 'accountClinical' || routeName === 'accountBilling') return 'clinical'
  if (routeName === 'accountBackups') return 'backups'
  if (routeName === 'accountIntegrations') return 'integrations'
  if (routeName?.startsWith('accountAdmin')) return 'admin'
  return 'patients'
})
</script>

<style scoped>
/* Full-bleed separator between the top menu and the page content. */
.account-tabs-bar {
  border-bottom: 1px solid var(--color-border);
  /* !important because UnifiedPaneToolbar's root carries Vuetify's `pl-1`
     utility, which is itself !important and would otherwise win. */
  padding-left: 65px !important; /* navigation rail (56px) + breathing room */
  /* No margins: the border has to sit directly on top of the content, with no
     strip of pane background showing between them. */
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}

/* Tab styling (moved with the markup from UserAccount.vue) */
.pane-toolbar-tabs :deep(.v-tab) {
  color: rgba(0, 0, 0, 0.67) !important;
  min-height: 36px;
  transition: all 0.2s ease;
  background-color: transparent !important;
}

.pane-toolbar-tabs :deep(.v-tab:hover) {
  background-color: transparent !important;
}

.pane-toolbar-tabs :deep(.v-tab--selected) {
  background-color: transparent !important;
  color: #2563eb !important;
}

.pane-toolbar-tabs :deep(.v-tab--selected .v-icon) {
  color: #2563eb !important;
}

.pane-toolbar-tabs :deep(.v-tab:not(.v-tab--selected)) {
  background-color: transparent !important;
  color: rgba(0, 0, 0, 0.67) !important;
  font-weight: normal !important;
}

.pane-toolbar-tabs :deep(.v-tab:not(.v-tab--selected) .v-icon) {
  color: rgba(0, 0, 0, 0.67) !important;
}

/* Move slider up inside the tab area */
.pane-toolbar-tabs :deep(.v-tab__slider) {
  bottom: 4px !important;
}
</style>
